import React, { useState, useEffect, useMemo } from "react";
import genStyles from "../../styles/General.module.css";
import { useParams, useNavigate } from "react-router-dom";
import PageTitle from "../../components/PageTitle";
import {
  Button,
  colorsTuple,
  Tabs,
  Alert,
  TextInput,
  Select,
} from "@mantine/core";
import { ReactComponent as DropdownIcon } from "../../assets/icons/Dropdown.svg";
import PopUp from "../../components/PopUp";
import { useDisclosure } from "@mantine/hooks";
import styles from "./recipeDetails.module.css";
import { RecipesRowType } from "../../types/recipes";
import { format } from "date-fns";
import {
  fakeBelts,
  fakeRecipeDetail,
  fakeColorOpt,
  fakeRecyclingOpt,
  fakeReuseOpt,
} from "../../data/fakeData";
import {
  PositionData,
  InputPositionData,
  Composition,
} from "../../types/recipes";
import RecipeDetailsList from "./recipeDetailsList";
import RecipesPopUp from "../recipes/recipesPopUp";
import { useRecipes } from "../../context/RecipeContext";
import { convertCompilerOptionsFromJson } from "typescript";
import axios from "axios";
import { Options } from "../../types/recipes";
import { CreateOptions } from "./recipeOptions";
import { Loader } from "@mantine/core";
import api from "../../api/axiosClient";

const UNIDENTIFIED_TEXTILE_TYPE_ID = 1;

const AR_DEFAULT_COMPOSITION = [
  { textil_type_id: UNIDENTIFIED_TEXTILE_TYPE_ID, post_consumer_recycled: 100 },
];

type InfoProps = {
  data: RecipesRowType;
  colorModelOptions: Options[];
  recyclingModelOptions: Options[];
  reuseModelOptions: Options[];
  onSave: (
    name: string,
    colorModelId: number,
    recyclingModelId: number,
    reuseModelId: number,
  ) => void;
};

const Info = ({
  data,
  colorModelOptions,
  recyclingModelOptions,
  reuseModelOptions,
  onSave,
}: InfoProps) => {
  const [name, setName] = useState(data?.name ?? "");
  const [colorModel, setColorModel] = useState<string | null>(
    data ? String(data.sorter_color_model_id) : null,
  );
  const [recyclingModel, setRecyclingModel] = useState<string | null>(
    data ? String(data.sorter_recycling_model_id) : null,
  );
  const [reuseModel, setReuseModel] = useState<string | null>(
    data ? String(data.sorter_reuse_model_id) : null,
  );

  useEffect(() => {
    setName(data?.name ?? "");
    setColorModel(data ? String(data.sorter_color_model_id) : null);
    setRecyclingModel(data ? String(data.sorter_recycling_model_id) : null);
    setReuseModel(data ? String(data.sorter_reuse_model_id) : null);
  }, [data]);

  return (
    <div className={styles.InfoRow}>
      <div className={styles.InfoGrid}>
        <div className={styles.Info}>
          <div className={styles.FirstColumn}>
            <span className={styles.InfoHeader}> Name</span>
            <TextInput
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
            />
          </div>
          <div className={styles.SecondColumn}>
            <span className={styles.InfoHeader}>Number Hoppers</span>
            <span className={styles.InfoContent}> {data?.num_hoppers} </span>
          </div>
          <div className={styles.ThirdColumn}>
            <span className={styles.InfoHeader}> Updated at </span>
            <span className={styles.InfoContent}>
              {" "}
              {format(
                data ? new Date(data.updated_at) : new Date(),
                "dd/MM/yyyy",
              )}{" "}
            </span>
          </div>
        </div>
        <div className={styles.Info}>
          <div className={styles.FirstColumn}>
            <span className={styles.InfoHeader}> Color Model</span>
            <Select
              data={colorModelOptions}
              value={colorModel}
              onChange={setColorModel}
              rightSection={
                <DropdownIcon width={19} height={19}></DropdownIcon>
              }
            />
          </div>
          <div className={styles.SecondColumn}>
            <span className={styles.InfoHeader}>Recycling Model</span>
            <Select
              data={recyclingModelOptions}
              value={recyclingModel}
              onChange={setRecyclingModel}
              rightSection={
                <DropdownIcon width={19} height={19}></DropdownIcon>
              }
            />
          </div>
          <div className={styles.ThirdColumn}>
            <span className={styles.InfoHeader}> Reuse Model</span>
            <Select
              data={reuseModelOptions}
              value={reuseModel}
              onChange={setReuseModel}
              rightSection={
                <DropdownIcon width={19} height={19}></DropdownIcon>
              }
            />
          </div>
        </div>
      </div>
      <Button
        className={styles.InfoSaveButton}
        onClick={() =>
          onSave(
            name,
            Number(colorModel),
            Number(recyclingModel),
            Number(reuseModel),
          )
        }
        disabled={!name.trim() || !colorModel || !recyclingModel || !reuseModel}
        variant="filled"
        color="#474973"
        radius="xl"
      >
        Save
      </Button>
    </div>
  );
};

const RecipeDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [openedDel, handlerDel] = useDisclosure(false);
  const [openedDupl, handlerDupl] = useDisclosure(false);
  const [data, setData] = useState<PositionData[]>();

  useEffect(() => {
    const fetchRecipesDetail = async () => {
      try {
        const response = await api.get(
          `/sorter/recipe/positions/get-by-recipe-id/${id}`,
        );
        const newData: PositionData[] = (
          response.data as InputPositionData[]
        ).map((hopper) => {
          return {
            id: hopper.position_id,
            position: hopper.position,
            product_color: hopper.product_color,
            product_materials: hopper.product_materials,
            colors: hopper.colors.map((colorInfo) => String(colorInfo.id)),
            recycling_categories: hopper.recycling_categories.map((recInfo) =>
              String(recInfo.id),
            ),
            reuse_categories: hopper.reuse_categories.map((reuseInfo) =>
              String(reuseInfo.id),
            ),
          };
        });

        setData(newData);
      } catch (error) {
        console.error("Error fetching recipes:", error);
        setData([]);
      }
    };
    fetchRecipesDetail();
  }, []);

  const { recipes, deleteRecipe, duplRecipe, updateRecipe } = useRecipes();

  const [modelOptions, setModelOptions] = useState<{
    color_models: Options[];
    recycling_models: Options[];
    reuse_models: Options[];
  }>({
    color_models: [],
    recycling_models: [],
    reuse_models: [],
  });

  useEffect(() => {
    const fetchModelOptions = async () => {
      const [colorModels, recyclingModels, reuseModels] = await Promise.all([
        api.get(`/sorter/model/color-models`),
        api.get(`/sorter/model/recycling-models`),
        api.get(`/sorter/model/reuse-models`),
      ]);
      const toOptions = (models: { id: number; name: string }[]) =>
        models.map(({ id, name }) => ({ value: String(id), label: name }));

      setModelOptions({
        color_models: toOptions(colorModels.data),
        recycling_models: toOptions(recyclingModels.data),
        reuse_models: toOptions(reuseModels.data),
      });
    };
    fetchModelOptions();
  }, []);

  const [options, setOptions] = useState<{
    color_options: Options[];
    recycling_options: Options[];
    reuse_options: Options[];
    textile_options: Options[];
    color_label_options: Options[];
  }>({
    color_options: [],
    recycling_options: [],
    reuse_options: [],
    textile_options: [],
    color_label_options: [],
  });

  useEffect(() => {
    if (!id) return;

    const fetchOptions = async () => {
      const result = await CreateOptions(Number(id));
      setOptions(result);
    };

    fetchOptions();
  }, []);

  const unusedRecyclingCategories = useMemo(() => {
    if (!data) return [];
    const usedIds = new Set(
      data.flatMap((hopper) => hopper.recycling_categories),
    );
    return options.recycling_options.filter(
      (option) => !usedIds.has(option.value),
    );
  }, [data, options.recycling_options]);

  const recipesInfo = recipes.filter((row) => {
    return row.id === Number(id);
  })[0];

  useEffect(() => {
    if (!recipesInfo) {
      navigate("/recipes");
    }
  }, [recipesInfo, navigate]);

  const delRecipe = async (id: number) => {
    await deleteRecipe(id);
    navigate(`/recipes`);
  };

  const duplRecipeSave = async (
    sorter_id: number,
    name: string,
    numHoppers: number,
    ColorModel: number,
    RecyclingModel: number,
    ReuseModel: number,
  ) => {
    //Get Recipe ID º
    const newId = await duplRecipe(
      name,
      numHoppers,
      ColorModel,
      RecyclingModel,
      ReuseModel,
    );
    //Get Position ID
    const response = await api.get(
      `/sorter/recipe/positions/get-by-recipe-id/${newId}`,
    );
    const positions: number[] = (response.data as InputPositionData[]).map(
      (hopper) => hopper.position_id,
    );

    //Save details to this new Positions
    if (data) {
      const positionUpdates = await Promise.all(
        data.map(async (element, i) => {
          const position = positions[i];
          const isReuseOnly =
            element.reuse_categories.length > 0 &&
            element.recycling_categories.length === 0;

          if (isReuseOnly) {
            const ar_product = {
              color_id: null,
              composition: AR_DEFAULT_COMPOSITION,
              product_type_id: 22,
              model_reference: "",
            };

            const ar_id = (await api.post("/products/create", ar_product))
              .data.id;

            return {
              position_id: position,
              colors: element.colors,
              recycling_categories: element.recycling_categories,
              reuse_categories: element.reuse_categories,
              wg_pre_product_id: null,
              wg_post_product_id: null,
              pg_pre_product_id: null,
              pg_post_product_id: null,
              ar_product_id: ar_id,
            };
          }

          const wg_pre_product = {
            color_id: element.product_color,
            composition: element.product_materials.map((material) => ({
              textil_type_id: material.textile_type_id,
              pre_consumer_recycled: material.percentage,
            })),
            product_type_id: 2,
            model_reference: "",
          };

          const wg_post_product = {
            color_id: element.product_color,
            composition: element.product_materials.map((material) => ({
              textil_type_id: material.textile_type_id,
              post_consumer_recycled: material.percentage,
            })),
            product_type_id: 2,
            model_reference: "",
          };

          const pg_pre_product = {
            color_id: element.product_color,
            composition: element.product_materials.map((material) => ({
              textil_type_id: material.textile_type_id,
              pre_consumer_recycled: material.percentage,
            })),
            product_type_id: 21,
            model_reference: "",
          };

          const pg_post_product = {
            color_id: element.product_color,
            composition: element.product_materials.map((material) => ({
              textil_type_id: material.textile_type_id,
              post_consumer_recycled: material.percentage,
            })),
            product_type_id: 21,
            model_reference: "",
          };

          const wg_pre_id = (await api.post("/products/create", wg_pre_product))
            .data.id;
          const wg_post_id = (
            await api.post("/products/create", wg_post_product)
          ).data.id;
          const pg_pre_id = (await api.post("/products/create", pg_pre_product))
            .data.id;
          const pg_post_id = (
            await api.post("/products/create", pg_post_product)
          ).data.id;

          return {
            position_id: position,
            colors: element.colors,
            recycling_categories: element.recycling_categories,
            reuse_categories: element.reuse_categories,
            wg_pre_product_id: wg_pre_id,
            wg_post_product_id: wg_post_id,
            pg_pre_product_id: pg_pre_id,
            pg_post_product_id: pg_post_id,
            ar_product_id: null,
          };
        }),
      );

      await api.put(`/sorter/recipe/positions/update-bulk/${newId}`, {
        positions: positionUpdates,
      });
    }
    //Navigate
    navigate(`/recipes/${newId}`);
  };

  const saveRecipeInfo = async (
    name: string,
    colorModelId: number,
    recyclingModelId: number,
    reuseModelId: number,
  ) => {
    if (!recipesInfo) return;
    await updateRecipe({
      ...recipesInfo,
      name,
      sorter_color_model_id: colorModelId,
      sorter_recycling_model_id: recyclingModelId,
      sorter_reuse_model_id: reuseModelId,
      updated_at: new Date(),
    });
  };

  const editRecipe = (
    id: number | undefined,
    position: number | undefined,
    color: string[],
    product_color: string | null,
    product_materials: Composition[],
    recycling: string[],
    reuse: string[],
  ) => {
    if (id && position) {
      setData((prevData) =>
        prevData?.map((el) =>
          el.id === id
            ? {
                id: id,
                position: position,
                product_color: product_color,
                product_materials: product_materials,
                colors: color,
                recycling_categories: recycling,
                reuse_categories: reuse,
              }
            : el,
        ),
      );
    }
  };

  const saveDB = async () => {
    const positions = (data || []).map((element) => ({
      position_id: element.id,
      colors: element.colors,
      recycling_categories: element.recycling_categories,
      reuse_categories: element.reuse_categories,
      wg_pre_product_id: null,
      wg_post_product_id: null,
      pg_pre_product_id: null,
      pg_post_product_id: null,
      ar_product_id: null,
    }));

    await api.put(`/sorter/recipe/positions/update-bulk/${id}`, { positions });

    navigate(`/recipes`);
  };

  const content = data ? (
    <RecipeDetailsList
      data={data}
      onSave={editRecipe}
      colorOpt={options.color_options}
      recyclingOpt={options.recycling_options}
      reuseOpt={options.reuse_options}
      textileOpt={options.textile_options}
      colorLabelOpt={options.color_label_options}
    />
  ) : (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <Loader color="pink" />
    </div>
  );

  return (
    <div>
      <PopUp
        opened={openedDel}
        close={handlerDel.close}
        size={700}
        title="Warning"
        onSave={() => delRecipe(Number(id))}
        button1="Delete"
      >
        Are you sure you want to delete this recipe?
      </PopUp>
      <RecipesPopUp
        opened={openedDupl}
        close={handlerDupl.close}
        title="Duplicate"
        onSave={duplRecipeSave}
        sorterInfo={recipesInfo}
        defName={recipesInfo?.name + " - copy"}
      ></RecipesPopUp>
      <PageTitle title="Recipe Details"></PageTitle>

      <div className={genStyles.PageMain}>
        <Info
          data={recipesInfo}
          colorModelOptions={modelOptions.color_models}
          recyclingModelOptions={modelOptions.recycling_models}
          reuseModelOptions={modelOptions.reuse_models}
          onSave={saveRecipeInfo}
        ></Info>
        {unusedRecyclingCategories.length > 0 && (
          <Alert color="yellow" title="Recycling categories not sorted" mb="md">
            {unusedRecyclingCategories.map((category) => (
              <div key={category.value}>
                {category.label} is not being sorted by any hopper, it will fall
                to the end of the belt.
              </div>
            ))}
          </Alert>
        )}
        {content}
        <div className={genStyles.AddButtonWrapper}>
          <Button
            className={genStyles.LeftButton}
            onClick={handlerDel.open}
            variant="filled"
            color="#A61C3C"
            radius="xl"
          >
            Delete
          </Button>

          <Button
            className={genStyles.AddButton}
            onClick={handlerDupl.open}
            variant="filled"
            color="#8A8CB5"
            radius="xl"
          >
            Duplicate
          </Button>
          <Button
            className={genStyles.AddButton}
            onClick={() => {
              duplRecipeSave(
                -1,
                recipesInfo.name,
                recipesInfo.num_hoppers,
                recipesInfo.sorter_color_model_id,
                recipesInfo.sorter_recycling_model_id,
                recipesInfo.sorter_reuse_model_id,
              );
              delRecipe(Number(id));
            }}
            variant="filled"
            color="#474973"
            radius="xl"
          >
            Save
          </Button>
          <Button
            className={genStyles.AddButton}
            onClick={() => {
              navigate(`/recipes`);
            }}
            variant="outline"
            color="#474973"
            radius="xl"
          >
            Back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RecipeDetailsPage;
